<script setup>
import { useStore } from 'vuex';
import { SEARCH_MIN_TERM_LENGTH } from '@maison/helpers/search-helper';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import PictureLazy from '@maison/components/common/PictureLazy.vue';
import ProductSlider from '@maison/components/organisms/ProductSlider.vue';
import { PhxIcon } from '@phx-styleguides/jlc';
import ProductCard from '@maison/components/product/ProductCard.vue';

const props = defineProps({
  collections: {
    type: Array,
    default: () => [],
  },
  suggestedProducts: {
    type: Array,
    default: () => [],
  },
  pushCards: {
    type: Array,
    default: () => [],
  },
  suggestionAllProductsLink: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { t } = useI18n();

const isLoading = computed(() => store.state.search.isLoading);
const query = computed(() => store.state.search.query);
const isResultsComponentPresent = computed(() => store.state.search.isResultsComponentPresent);
const totalResults = computed(() => store.getters['search/totalResults']);
const hasQuery = computed(() => query.value !== '');
const hasCollections = computed(() => props.collections.length > 0);
const hasSuggestedProducts = computed(() => props.suggestedProducts.length > 0);
const shouldDisplayNoResults = computed(() => totalResults.value === 0 || query.value.length < SEARCH_MIN_TERM_LENGTH);
const noResultsTitle = computed(() => {
  return hasQuery.value ? t('Search.noResultTitleWithQuery', { search: query.value }) : t('Search.noResultTitle');
});
</script>

<template>
  <div v-if="isLoading || !isResultsComponentPresent" class="flex flex-grid--center items-center" style="height: 50vh">
    <div class="loader loader--dark">
      <div class="loader__inner"></div>
    </div>
  </div>

  <template v-else-if="shouldDisplayNoResults">
    <section class="section">
      <section class="header-section container">
        <h3 class="header-section__title">{{ noResultsTitle }}</h3>
      </section>
    </section>

    <section v-if="hasCollections" class="section">
      <div class="container">
        <div class="h4 mb-xs">{{ $t('Search.collections.title') }}</div>

        <div data-simplebar="true" class="">
          <ul class="scrollable-actions-list mega-menu__list-actions">
            <li v-for="collection in collections" :key="collection.id">
              <a
                :href="collection.url"
                type="button"
                class="btn btn--default btn--sm btn--outlined mega-menu__list-actions-btn"
              >
                <span class="btn__text">{{ collection.title }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section v-if="hasSuggestedProducts" class="section">
      <section class="header-section container pt-0">
        <h3 class="header-section__title">{{ $t('Search.youMayAlsoLike') }}</h3>

        <div v-if="suggestionAllProductsLink" class="header-section__link hidden md:block">
          <a :href="suggestionAllProductsLink" class="btn btn--default btn--sm btn--negative">
            <span class="btn__text">{{ $t('Search.panel.products.showAll') }}</span>
          </a>
        </div>
      </section>

      <div class="container">
        <div class="md:hidden">
          <div class="mixed-grid md:hidden">
            <product-card v-for="product in suggestedProducts" :key="product.modelReference" v-bind="product" />
          </div>

          <a
            v-if="suggestionAllProductsLink"
            :href="suggestionAllProductsLink"
            type="button"
            class="btn btn--default btn--full btn--negative mt-xs"
          >
            <span class="btn__text">{{ $t('Search.panel.products.showAll') }}</span>
          </a>
        </div>

        <div class="hidden md:block">
          <product-slider slider-id="search-suggested-products-slider" :product-pages="suggestedProducts" />
        </div>
      </div>
    </section>

    <section class="section section--top">
      <div class="container">
        <div class="search-mosaic-grid">
          <div v-for="(card, index) in pushCards" :key="`search-result-push-card-${index}`" class="card">
            <div class="card__media">
              <div class="picture picture--cover">
                <picture-lazy v-bind="card.image" img-class="lazyload--fade" lazy />
              </div>
            </div>

            <div class="card__details">
              <div v-if="card.title" class="card__title h5 hidden lg:block">{{ card.title }}</div>

              <a :href="card.link" class="btn btn--link btn--icon card__button expand-target">
                <span class="btn__text">{{ card.ctaText }}</span>

                <span class="btn__icon">
                  <phx-icon icon="chevron-10" bidirectional />
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
</template>
